import React, { useState } from 'react';
import { FiTrendingUp, FiAlertCircle, FiCheckCircle, FiTarget, FiBookOpen, FiUsers, FiClock, FiBarChart2 } from 'react-icons/fi';
import Button from '../common/Button';
import Badge from '../common/Badge';
import LoadingSpinner from '../common/LoadingSpinner';
import './AIInsights.css';

const AIInsights = ({ insights, questionIds, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  // Removed unused expandedSections state to satisfy ESLint

  if (!insights) {
    return (
      <div className="ai-insights ai-insights--loading">
        <LoadingSpinner />
        <p>Generating AI insights...</p>
      </div>
    );
  }

  // Removed unused toggleSection handler (no section toggling implemented)

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'danger';
  };

  const getScoreLabel = (score) => {
    if (score >= 0.8) return 'Excellent';
    if (score >= 0.6) return 'Good';
    if (score >= 0.4) return 'Fair';
    return 'Needs Improvement';
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <FiBarChart2 /> },
    { id: 'quality', label: 'Quality Analysis', icon: <FiCheckCircle /> },
    { id: 'difficulty', label: 'Difficulty Assessment', icon: <FiTarget /> },
    { id: 'recommendations', label: 'Recommendations', icon: <FiTrendingUp /> },
    { id: 'performance', label: 'Performance Insights', icon: <FiUsers /> }
  ];

  return (
    <div className="ai-insights">
      {/* Header */}
      <div className="ai-insights__header">
        <div className="ai-insights__title">
          <h2>AI Question Insights</h2>
          <p>{questionIds.length} questions analyzed</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="ai-insights__tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`ai-insights__tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="ai-insights__content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="ai-insights__overview">
            {/* Summary Cards */}
            <div className="insights-grid">
              <div className="insight-card">
                <div className="insight-card__icon">
                  <FiCheckCircle className="text-success" />
                </div>
                <div className="insight-card__content">
                  <h3>Overall Quality</h3>
                  <div className="insight-card__value">
                    {Math.round(insights.overallQuality * 100)}%
                  </div>
                  <Badge variant={getScoreColor(insights.overallQuality)}>
                    {getScoreLabel(insights.overallQuality)}
                  </Badge>
                </div>
              </div>

              <div className="insight-card">
                <div className="insight-card__icon">
                  <FiTarget className="text-warning" />
                </div>
                <div className="insight-card__content">
                  <h3>Avg Difficulty</h3>
                  <div className="insight-card__value">
                    {insights.averageDifficulty || 'N/A'}
                  </div>
                  <Badge variant="outline">
                    {insights.difficultyDistribution?.mode || 'Mixed'}
                  </Badge>
                </div>
              </div>

              <div className="insight-card">
                <div className="insight-card__icon">
                  <FiClock className="text-info" />
                </div>
                <div className="insight-card__content">
                  <h3>Avg Time</h3>
                  <div className="insight-card__value">
                    {Math.round(insights.averageTime || 0)}s
                  </div>
                  <Badge variant="outline">
                    Per Question
                  </Badge>
                </div>
              </div>

              <div className="insight-card">
                <div className="insight-card__icon">
                  <FiUsers className="text-primary" />
                </div>
                <div className="insight-card__content">
                  <h3>Usage Rate</h3>
                  <div className="insight-card__value">
                    {Math.round(insights.averageUsage || 0)}
                  </div>
                  <Badge variant="outline">
                    Times Used
                  </Badge>
                </div>
              </div>
            </div>

            {/* Key Insights */}
            {insights.keyInsights && insights.keyInsights.length > 0 && (
              <div className="insights-section">
                <h3>Key Insights</h3>
                <div className="insights-list">
                  {insights.keyInsights.map((insight, index) => (
                    <div key={index} className="insight-item">
                      <div className="insight-item__icon">
                        {insight.type === 'positive' ? (
                          <FiCheckCircle className="text-success" />
                        ) : insight.type === 'warning' ? (
                          <FiAlertCircle className="text-warning" />
                        ) : (
                          <FiTrendingUp className="text-info" />
                        )}
                      </div>
                      <div className="insight-item__content">
                        <p>{insight.message}</p>
                        {insight.impact && (
                          <Badge variant="light" size="sm">
                            Impact: {insight.impact}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quality Analysis Tab */}
        {activeTab === 'quality' && (
          <div className="ai-insights__quality">
            {/* Quality Metrics */}
            <div className="quality-metrics">
              <h3>Quality Breakdown</h3>
              <div className="metrics-grid">
                {insights.qualityMetrics && Object.entries(insights.qualityMetrics).map(([metric, score]) => (
                  <div key={metric} className="metric-item">
                    <div className="metric-item__header">
                      <span className="metric-item__label">
                        {metric.charAt(0).toUpperCase() + metric.slice(1).replace(/([A-Z])/g, ' $1')}
                      </span>
                      <Badge variant={getScoreColor(score)}>
                        {Math.round(score * 100)}%
                      </Badge>
                    </div>
                    <div className="metric-item__bar">
                      <div 
                        className={`metric-item__fill metric-item__fill--${getScoreColor(score)}`}
                        style={{ width: `${score * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quality Issues */}
            {insights.qualityIssues && insights.qualityIssues.length > 0 && (
              <div className="quality-issues">
                <h3>Quality Issues Found</h3>
                <div className="issues-list">
                  {insights.qualityIssues.map((issue, index) => (
                    <div key={index} className={`issue-item issue-item--${issue.severity}`}>
                      <div className="issue-item__header">
                        <FiAlertCircle />
                        <span className="issue-item__title">{issue.title}</span>
                        <Badge variant={issue.severity === 'high' ? 'danger' : issue.severity === 'medium' ? 'warning' : 'secondary'}>
                          {issue.severity}
                        </Badge>
                      </div>
                      <p className="issue-item__description">{issue.description}</p>
                      {issue.affectedQuestions && (
                        <p className="issue-item__affected">
                          Affects {issue.affectedQuestions} question(s)
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Difficulty Assessment Tab */}
        {activeTab === 'difficulty' && (
          <div className="ai-insights__difficulty">
            {/* Difficulty Distribution */}
            {insights.difficultyDistribution && (
              <div className="difficulty-distribution">
                <h3>Difficulty Distribution</h3>
                <div className="distribution-chart">
                  {Object.entries(insights.difficultyDistribution).map(([level, count]) => {
                    if (level === 'mode') return null;
                    const percentage = (count / questionIds.length) * 100;
                    return (
                      <div key={level} className="distribution-item">
                        <div className="distribution-item__header">
                          <span className="distribution-item__label">
                            {level.charAt(0).toUpperCase() + level.slice(1)}
                          </span>
                          <span className="distribution-item__count">
                            {count} ({Math.round(percentage)}%)
                          </span>
                        </div>
                        <div className="distribution-item__bar">
                          <div 
                            className={`distribution-item__fill distribution-item__fill--${level}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Difficulty Analysis */}
            {insights.difficultyAnalysis && (
              <div className="difficulty-analysis">
                <h3>Difficulty Analysis</h3>
                <div className="analysis-content">
                  <p>{insights.difficultyAnalysis.summary}</p>
                  
                  {insights.difficultyAnalysis.recommendations && (
                    <div className="analysis-recommendations">
                      <h4>Recommendations:</h4>
                      <ul>
                        {insights.difficultyAnalysis.recommendations.map((rec, index) => (
                          <li key={index}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recommendations Tab */}
        {activeTab === 'recommendations' && (
          <div className="ai-insights__recommendations">
            {insights.recommendations && insights.recommendations.length > 0 ? (
              <div className="recommendations-list">
                {insights.recommendations.map((rec, index) => (
                  <div key={index} className={`recommendation-item recommendation-item--${rec.priority}`}>
                    <div className="recommendation-item__header">
                      <div className="recommendation-item__icon">
                        {rec.type === 'improvement' ? (
                          <FiTrendingUp />
                        ) : rec.type === 'warning' ? (
                          <FiAlertCircle />
                        ) : (
                          <FiCheckCircle />
                        )}
                      </div>
                      <div className="recommendation-item__title">
                        <h4>{rec.title}</h4>
                        <Badge variant={rec.priority === 'high' ? 'danger' : rec.priority === 'medium' ? 'warning' : 'secondary'}>
                          {rec.priority} priority
                        </Badge>
                      </div>
                    </div>
                    
                    <p className="recommendation-item__description">{rec.description}</p>
                    
                    {rec.actionItems && rec.actionItems.length > 0 && (
                      <div className="recommendation-item__actions">
                        <h5>Action Items:</h5>
                        <ul>
                          {rec.actionItems.map((action, actionIndex) => (
                            <li key={actionIndex}>{action}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {rec.expectedImpact && (
                      <div className="recommendation-item__impact">
                        <strong>Expected Impact:</strong> {rec.expectedImpact}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-recommendations">
                <FiCheckCircle size={48} className="text-success" />
                <h3>Great Job!</h3>
                <p>No specific recommendations at this time. Your questions are performing well.</p>
              </div>
            )}
          </div>
        )}

        {/* Performance Insights Tab */}
        {activeTab === 'performance' && (
          <div className="ai-insights__performance">
            {/* Performance Metrics */}
            {insights.performanceMetrics && (
              <div className="performance-metrics">
                <h3>Performance Metrics</h3>
                <div className="metrics-grid">
                  <div className="metric-card">
                    <h4>Success Rate</h4>
                    <div className="metric-card__value">
                      {Math.round((insights.performanceMetrics.correctAnswers / insights.performanceMetrics.totalAttempts) * 100)}%
                    </div>
                    <p>Students answering correctly</p>
                  </div>
                  
                  <div className="metric-card">
                    <h4>Avg Response Time</h4>
                    <div className="metric-card__value">
                      {Math.round(insights.performanceMetrics.averageTime)}s
                    </div>
                    <p>Time per question</p>
                  </div>
                  
                  <div className="metric-card">
                    <h4>Skip Rate</h4>
                    <div className="metric-card__value">
                      {Math.round((insights.performanceMetrics.skipped / insights.performanceMetrics.totalAttempts) * 100)}%
                    </div>
                    <p>Questions skipped</p>
                  </div>
                </div>
              </div>
            )}

            {/* Learning Insights */}
            {insights.learningInsights && (
              <div className="learning-insights">
                <h3>Learning Insights</h3>
                <div className="insights-content">
                  {insights.learningInsights.map((insight, index) => (
                    <div key={index} className="learning-insight-item">
                      <div className="learning-insight-item__icon">
                        <FiBookOpen />
                      </div>
                      <div className="learning-insight-item__content">
                        <h4>{insight.title}</h4>
                        <p>{insight.description}</p>
                        {insight.suggestions && (
                          <ul className="learning-insight-item__suggestions">
                            {insight.suggestions.map((suggestion, suggestionIndex) => (
                              <li key={suggestionIndex}>{suggestion}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="ai-insights__footer">
        <div className="ai-insights__timestamp">
          Generated on {new Date().toLocaleString()}
        </div>
        
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
};

export default AIInsights;